import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, ChevronDown } from "lucide-react";
import { BrandMark } from "@/components/site/BrandMark";
import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/lib/auth-context";

type NavLeaf = {
  to: string;
  label: string;
  desc?: string;
  eyebrow?: string;
  emphasize?: boolean;
  pulse?: boolean;
  section?: string;
};
type NavGroup = { label: string; group: string; children: NavLeaf[] };
type NavItem = NavLeaf | NavGroup;

const nav: NavItem[] = [
  { to: "/", label: "Home" },
  {
    label: "Products",
    group: "products",
    children: [
      { to: "/terminal", label: "XAU Terminal™", section: "The platform", eyebrow: "One membership, one workspace", emphasize: true, desc: "The unified gold platform. Every engine on the right runs inside it: confluence, chart audits, the live desk and risk." },
      { to: "/macrolens", label: "XAU MacroLens™", section: "Integrated core engines", eyebrow: "Macro and sentiment confluence", desc: "One confluence score for gold, macro plus structure." },
      { to: "/platform/structurelens", label: "StructureLens™", eyebrow: "Institutional AI chart audits", desc: "Upload a chart for a structure and liquidity read." },
      { to: "/orion", label: "Orion AI", eyebrow: "Systematic execution suite", desc: "OTAS-01, OSAS-01 and OEAS-01 under one risk doctrine." },
    ],
  },
  {
    label: "Signals & Analysis",
    group: "signals",
    children: [
      { to: "/signals", label: "Live Desk Signals", eyebrow: "Free account", emphasize: true, desc: "Live XAUUSD desk decisions with alerts." },
      { to: "/weekly-outlook", label: "Weekly Gold Outlook", eyebrow: "Weekend Edition", desc: "Week ahead roadmap, liquidity map and trade review." },
      { to: "/xauusd-analysis-today", label: "Gold Analysis Today", desc: "Live price, session context and market pulse." },
      { to: "/xauusd-trading", label: "How to Trade Gold", desc: "The complete institutional XAUUSD guide." },
      { to: "/gold-trading-strategy", label: "Gold Trading Strategy", desc: "Structure, liquidity, confirmation and risk." },
      { to: "/what-is-xauusd", label: "What Is XAUUSD", desc: "Gold symbol, contracts and pip math." },
      { to: "/gold-analysis", label: "Market Analysis", desc: "Institutional XAUUSD structure reads." },
      { to: "/smart-money-concepts", label: "Smart Money Concepts", desc: "Structure, liquidity and POI framework for gold." },
    ],
  },
  {
    label: "Market Tools",
    group: "tools",
    children: [
      { to: "/position-size-calculator", label: "Position Size Calculator", section: "Execution & Technical", eyebrow: "Gold and Forex", emphasize: true, desc: "Lot size and prop firm risk limits." },
      { to: "/gold-pip-calculator", label: "Gold Pip Calculator", desc: "XAUUSD pip value, profit and spread cost." },
      { to: "/gold-margin-calculator", label: "Gold Margin Calculator", desc: "XAUUSD margin requirement by leverage." },
      { to: "/gold-daily-range-atr", label: "Gold Daily Range & ATR", desc: "Live ADR, ATR and range used today." },
      { to: "/xauusd-volatility-heatmap", label: "Gold Volatility Heatmap", desc: "Average range by weekday and hour." },
      { to: "/gold-trading-sessions", label: "Gold Trading Hours", desc: "Live sessions clock, overlaps and news freeze." },
      { to: "/gold-killzones-clock", label: "Gold Killzones Clock", desc: "Live ICT killzones and Silver Bullet hours." },
      { to: "/gold-cot-report", label: "Gold COT Report", section: "Macro & Fundamentals", desc: "Weekly CFTC institutional positioning." },
      { to: "/gold-inflation-cpi", label: "CPI Inflation Tracker", desc: "US inflation releases and gold reaction." },
      { to: "/gold-nfp-employment", label: "NFP Jobs Tracker", desc: "US jobs reports and gold reaction." },
      { to: "/gold-seasonality", label: "Gold Seasonality", desc: "Historical monthly gold returns." },
      { to: "/gold-retail-sentiment", label: "Gold Retail Sentiment", desc: "Live XAUUSD long vs short crowd positioning." },
      { to: "/gold-options-sentiment", label: "Gold Options Sentiment", desc: "Put/call ratio, call and put walls, max pain." },
      { to: "/gold-real-yield", label: "Real Yield Tracker", desc: "US 10Y TIPS real yield, gold's key macro driver." },
      { to: "/gold-central-bank-reserves", label: "Central Bank Gold Reserves", desc: "Official gold holdings and buying by country." },
    ],
  },
  {
    label: "Performance",
    group: "performance",
    children: [
      { to: "/track-record", label: "Performance Record", eyebrow: "2024 to 2026", emphasize: true, pulse: true, desc: "Three verified years of closed XAUUSD outcomes." },
      { to: "/case-studies", label: "Case Studies", desc: "Closed live trades with candle by candle replay." },
      { to: "/validation", label: "Validation Hub", desc: "Methodology, shadow vs live, and result audits." },
    ],
  },
  { to: "/education", label: "Academy" },
  { to: "/pricing", label: "Membership" },
];

function isLeaf(item: NavItem): item is NavLeaf {
  return "to" in item;
}

function splitSections(children: NavLeaf[]): { name: string; items: NavLeaf[] }[] {
  const sections: { name: string; items: NavLeaf[] }[] = [];
  for (const child of children) {
    if (child.section || sections.length === 0) {
      sections.push({ name: child.section ?? "", items: [] });
    }
    sections[sections.length - 1].items.push(child);
  }
  return sections;
}

const desktopLeafCls =
  "relative text-sm font-medium text-muted-foreground transition-colors hover:text-foreground data-[status=active]:text-gold after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-gold after:transition-transform hover:after:scale-x-100 data-[status=active]:after:scale-x-100";

export function Header() {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const { user, isHydrated } = useAuth();
  const waitedForHydrationRef = useRef(!isHydrated);
  const shouldAnimate = waitedForHydrationRef.current;

  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isActivePath = (to: string) =>
    to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/");

  const activeGroup = useMemo(() => {
    for (const item of nav) {
      if (!isLeaf(item)) {
        if (item.children.some((c) => pathname.startsWith(c.to))) return item.group;
      }
    }
    return undefined;
  }, [pathname]);

  // Auth-aware Platform target.
  const platformTo = user ? "/dashboard" : "/platform";

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2 group">
          <BrandMark />
          <span className="font-display text-xl font-bold tracking-tight">
            XAU <span className="text-gradient-gold">Pro</span> Academy
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          {nav.map((item) => {
            if (isLeaf(item)) {
              if (item.label === "Platform") {
                return (
                  <Link
                    key={item.label}
                    to={platformTo}
                    activeOptions={{ exact: false }}
                    className={desktopLeafCls}
                  >
                    {item.label}
                  </Link>
                );
              }
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  activeOptions={{ exact: item.to === "/" }}
                  className={desktopLeafCls}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                  aria-expanded={openMenu === item.label}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                {openMenu === item.label && (() => {
                  const sections = splitSections(item.children);
                  const multi = sections.length > 1;
                  return (
                  <div
                    className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 ${
                      multi ? "w-[44rem]" : "w-[24rem]"
                    }`}
                  >
                      <div
                      className={`rounded-xl border border-border/70 bg-background shadow-xl backdrop-blur-xl ${
                        multi ? "flex items-stretch" : "p-2"
                      }`}
                    >
                      {sections.map((section, sIdx) => (
                        <div
                          key={section.name || sIdx}
                          className={`p-2 ${multi ? "flex-1" : ""} ${
                            multi && sIdx > 0 ? "border-l border-border/60" : ""
                          }`}
                        >
                          {section.name && (
                            <div className="px-3 pb-1 pt-1 font-mono text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground">
                              {section.name}
                            </div>
                          )}
                          {section.items.map((child) => {
                            const isActive = isActivePath(child.to);
                            return (
                            <Link
                              key={child.to}
                              to={child.to}
                              onClick={() => setOpenMenu(null)}
                              className={`group block rounded-lg border-l-2 px-3 py-2.5 transition hover:bg-secondary ${
                                isActive ? "border-gold/70 text-gold" : "border-transparent"
                              }`}
                            >
                              {child.eyebrow && (
                                <div
                                  className={`font-mono text-[9.5px] uppercase tracking-[0.2em] ${
                                    isActive ? "text-gold" : "text-muted-foreground"
                                  }`}
                                >
                                  {child.eyebrow}
                                </div>
                              )}
                              <div
                                className={`flex items-center gap-2 text-sm font-semibold ${isActive ? "text-gold" : "text-foreground"}`}
                              >
                                {child.label}
                                {child.pulse && (
                                  <span
                                    aria-hidden
                                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-gold animate-glow-dot"
                                  />
                                )}
                              </div>
                              {child.desc && (
                                <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                                  {child.desc}
                                </div>
                              )}
                            </Link>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })()}
              </div>
            );
          })}
        </nav>

        <div className="hidden md:flex md:items-center md:gap-3">
          {!isHydrated ? (
            <div aria-hidden className="h-9 w-[168px] rounded-md bg-white/5" />
          ) : (
            <div className={`flex items-center gap-3 ${shouldAnimate ? "animate-in fade-in duration-500" : ""}`}>
              {user ? (
                <Button asChild variant="gold" size="sm">
                  <Link to="/dashboard">XAU Terminal™</Link>
                </Button>
              ) : (
                <>
                  <Link
                    to="/auth"
                    search={{ tab: "login" }}
                    className="text-sm font-medium text-muted-foreground transition hover:text-foreground"
                  >
                    Log In
                  </Link>
                  <Button asChild variant="gold" size="sm">
                    <Link to="/auth" search={{ tab: "signup" }}>Start Free</Link>
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <button className="md:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border/60 bg-background md:hidden">
          {/* Sticky CTA bar inside the mobile sheet */}
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border/60 bg-background/95 px-4 py-2.5 backdrop-blur-xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Menu
            </div>
            {isHydrated &&
              (user ? (
                <Button asChild variant="gold" size="sm">
                  <Link to="/dashboard" onClick={() => setOpen(false)}>
                    XAU Terminal™
                  </Link>
                </Button>
              ) : (
                <Button asChild variant="gold" size="sm">
                  <Link to="/auth" search={{ tab: "signup" }} onClick={() => setOpen(false)}>
                    Start Free
                  </Link>
                </Button>
              ))}
          </div>

          <div className="container mx-auto flex flex-col gap-1 px-4 py-3">
            <Accordion type="single" collapsible defaultValue={activeGroup}>
              {nav.map((item) => {
                if (isLeaf(item)) {
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      activeOptions={{ exact: item.to === "/" }}
                      onClick={() => setOpen(false)}
                      className="block rounded-md border-l-2 border-transparent px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground data-[status=active]:border-gold data-[status=active]:text-gold"
                    >
                      {item.label}
                    </Link>
                  );
                }
                const group = item;
                return (
                  <AccordionItem key={group.group} value={group.group} className="border-border/60">
                    <AccordionTrigger className="px-3 py-2.5 text-sm font-medium text-foreground hover:no-underline">
                      {group.label}
                    </AccordionTrigger>
                    <AccordionContent className="pb-1">
                      {group.children.map((child, idx) => (
                        <div key={child.to}>
                        {child.section && (
                          <div
                            className={`px-3 pb-1 font-mono text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground ${
                              idx > 0 ? "mt-2 border-t border-border/60 pt-3" : "pt-1"
                            }`}
                          >
                            {child.section}
                          </div>
                        )}
                        <Link
                          to={child.to}
                          onClick={() => setOpen(false)}
                          className="block rounded-md border-l-2 border-transparent px-3 py-2 transition-colors hover:bg-secondary data-[status=active]:border-gold data-[status=active]:bg-secondary/60"
                        >
                          <div className={`flex items-center gap-2 text-sm font-medium ${isActivePath(child.to) ? "text-gold" : "text-foreground"}`}>
                            {child.label}
                            {child.pulse && (
                              <span
                                aria-hidden
                                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-gold animate-glow-dot"
                              />
                            )}
                          </div>
                          {child.desc && (
                            <div className="text-xs text-muted-foreground">{child.desc}</div>
                          )}
                        </Link>
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {/* Bottom auth fallback */}
            {isHydrated && !user && (
              <Link
                to="/auth"
                search={{ tab: "login" }}
                onClick={() => setOpen(false)}
                className="mt-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Log In
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
